import React from "react";
import { Button } from "@/components";
import { Edit, Trash2, Eye } from "lucide-react";
import type { Campaign } from "../interfaces";

interface CampaignCardProps {
  campaign: Campaign;
  onEdit: (campaign: Campaign) => void;
  onDelete: (campaignId: number) => void;
  onView?: (campaign: Campaign) => void;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({
  campaign,
  onEdit,
  onDelete,
  onView,
}) => {
  const duration =
    campaign.config?.duration &&
    `${campaign.config.duration.periodCount} ${campaign.config.duration.period}`;

  return (
    <div
      className="border border-input rounded-lg p-4 sm:p-6 bg-card hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView?.(campaign)}
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold mb-2 truncate">{campaign.title}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {campaign.description}
          </p>
        </div>
        <div
          className="flex gap-2 sm:ml-4 shrink-0 justify-end sm:justify-start"
          onClick={(e) => e.stopPropagation()}
        >
          {onView && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onView(campaign)}
              title="View campaign details"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(campaign)}
            title="Edit campaign"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(campaign.id)}
            title="Delete campaign"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {campaign.images && campaign.images.length > 0 && (
        <div className="mb-4">
          <div className="grid grid-cols-3 gap-2">
            {campaign.images.slice(0, 3).map((image, index) => (
              <div
                key={index}
                className="aspect-video rounded-md overflow-hidden bg-muted"
              >
                <img
                  src={image}
                  alt={`${campaign.title} image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
          {campaign.images.length > 3 && (
            <p className="text-xs text-muted-foreground mt-2">
              +{campaign.images.length - 3} more images
            </p>
          )}
        </div>
      )}

      {campaign.videos && campaign.videos.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground">
            {campaign.videos.length} video{campaign.videos.length > 1 ? "s" : ""}
          </p>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-input flex-wrap gap-2">
        <span>Created: {new Date(campaign.createdAt).toLocaleDateString()}</span>
        {duration && (
          <span className="px-2 py-1 bg-muted rounded text-xs font-medium">
            {duration}
          </span>
        )}
        {campaign.strategyIds && campaign.strategyIds.length > 0 && (
          <span className="px-2 py-1 bg-primary/10 text-primary rounded">
            {campaign.strategyIds.length} plan
            {campaign.strategyIds.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
};
